import React, { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { useContent } from "@/hooks/useContent";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Instagram, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageSEO from "@/components/PageSEO";

const TOP_IMG = "/assets/TOP IMAGE REBIRTHING GENESIS.jpg";
const LOWER_IMG = "/assets/LOWER IMAGE REBIRTHING GENESIS.jpg";

const faqs = [
  { q: "What is Rebirthing Genesis?", a: "A modality combining conscious connected breathwork, nervous system regulation, somatic integration, and ceremonial support — held for the process of becoming who you already are." },
  { q: "Is it safe for beginners?", a: "Yes. Every session is held by a trained facilitator with medical intake beforehand. We adapt for beginners and integrate slowly." },
  { q: "Do I need to prepare?", a: "Come hydrated, lightly fed, and open. We provide a preparation ritual 48 hours before your session." },
  { q: "Can I combine this with a retreat?", a: "Most retreats include a full Rebirthing Genesis arc. You can also book private sessions online or in-person at the Sanctuary." },
  { q: "How do I get to the Sanctuary?", a: "Fly into Bogotá (BOG) — El Dorado International. From Bogotá, collective ground transport can be arranged to the sanctuary in Cundinamarca (approximately 2 hours)." },
];

function BookSessionDialog() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    format: "in_person",
    intention: ""
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);

    try {
      const messageBody =
        `SESSION BOOKING REQUEST — Rebirthing Genesis\n` +
        `Format: ${form.format === "in_person" ? "In-person (Sanctuary, Cundinamarca)" : "Online"}\n` +
        `Phone: ${form.phone || "(not provided)"}\n\n` +
        `Intention:\n${form.intention || "(not provided)"}`;

      await api.post("/contact", {
        name: form.name,
        email: form.email,
        subject: "Book a Rebirthing Genesis Session",
        message: messageBody,
      });

      toast.success("Session request received. Our facilitator will reach out within 3 days.");

      setForm({
        name: "",
        email: "",
        phone: "",
        format: "in_person",
        intention: ""
      });

      setOpen(false);
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) ||
          "Something went wrong. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="rebirthing-book-session"
          className="pill pill-primary"
        >
          Book a Session
        </button>
      </DialogTrigger>

      <DialogContent className="bg-bone max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            Book a Rebirthing Genesis Session
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="overline mb-2 block">Full name</Label>
              <Input
                required
                data-testid="book-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div>
              <Label className="overline mb-2 block">Email</Label>
              <Input
                type="email"
                required
                data-testid="book-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label className="overline mb-2 block">Phone (optional)</Label>
            <Input
              data-testid="book-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>

          <div>
            <Label className="overline mb-2 block">Session Format</Label>

            <div className="inline-flex bg-surface border border-line rounded-full p-1">
              <button
                type="button"
                onClick={() => setForm({ ...form, format: "in_person" })}
                data-testid="book-format-in-person"
                className={`px-4 py-1.5 rounded-full text-sm ${
                  form.format === "in_person"
                    ? "bg-forest text-bone"
                    : "text-ink"
                }`}
              >
                In-person (Sanctuary)
              </button>

              <button
                type="button"
                onClick={() => setForm({ ...form, format: "online" })}
                data-testid="book-format-online"
                className={`px-4 py-1.5 rounded-full text-sm ${
                  form.format === "online"
                    ? "bg-forest text-bone"
                    : "text-ink"
                }`}
              >
                Online
              </button>
            </div>
          </div>

          <div>
            <Label className="overline mb-2 block">
              Your intention (what draws you to this?)
            </Label>
            <Textarea
              rows={4}
              data-testid="book-intention"
              value={form.intention}
              onChange={(e) =>
                setForm({ ...form, intention: e.target.value })
              }
            />
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="book-submit"
            className="w-full bg-forest text-bone hover:bg-ink rounded-full py-6"
          >
            {busy ? "Sending..." : "Send Session Request"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Rebirthing() {
  const { t } = useContent("rebirthing");

  return (
    <div>
      <PageSEO
        title={"Rebirthing Genesis | Breathwork & Nervous System Healing"}
        description={
          "Rebirthing Genesis at Celestial Seed Sanctuary: conscious connected breathwork, polyvagal-informed practice, and ceremonial integration. Sessions in-person and online."
        }
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-12">
        <div
          className="overline mb-6"
          data-testid="rebirthing-overline"
        >
          {t("hero.overline", "The Modality")}
        </div>

        <h1
          className="font-serif text-5xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl"
          data-testid="rebirthing-title"
        >
          {t("hero.title", "Rebirthing Genesis")}
        </h1>

        <p
          className="text-ink-soft text-xl mt-8 max-w-2xl"
          data-testid="rebirthing-lead"
        >
          {t(
            "hero.lead",
            "Breath is the doorway. Nervous system regulation is the ground. What arises is remembering."
          )}
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-start py-12">
        <img
          src={TOP_IMG}
          alt="Rebirthing Genesis breathwork and meditation circle"
          className="rounded-3xl aspect-[4/5] object-cover"
        />

        <div className="space-y-8">
          {[
            {
              t: "Breathwork",
              b: "Conscious connected breath sessions — 60 to 90 minutes — that soften stored tension and open forgotten rooms."
            },
            {
              t: "Nervous System Regulation",
              b: "Polyvagal-informed practice. Learn the map of your own regulation and how to come home to it."
            },
            {
              t: "Ceremonial Integration",
              b: "Every session is held with intention, music, and a slow return. We do not extract insight; we let it root."
            },
            {
              t: "Private Sessions & Workshops",
              b: "1:1 sessions, small circles, and multi-day workshops at the Sanctuary (Cundinamarca) and online."
            },
          ].map((c) => (
            <div key={c.t} className="border-t border-line pt-6">
              <div className="font-serif text-2xl">{c.t}</div>
              <p className="text-ink-soft mt-2 leading-relaxed">{c.b}</p>
            </div>
          ))}

          <div className="pt-4 flex gap-3 flex-wrap">
            <BookSessionDialog />
            <Link
              to="/retreats"
              data-testid="rebirthing-see-retreats"
              className="pill pill-outline"
            >
              See Retreats
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <img
          src={LOWER_IMG}
          alt="Rebirthing Genesis group ceremony in a tropical setting"
          className="w-full rounded-3xl object-cover max-h-[720px]"
        />
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
        <div className="rounded-2xl border border-line bg-surface p-8">
          <div className="overline mb-3">Reach us</div>

          <div className="space-y-3 text-ink">
            <div className="flex items-center gap-3">
              <Mail
                className="h-4 w-4 text-terracotta"
                strokeWidth={1.5}
              />
              <a
                href="mailto:newearth144@proton.me"
                className="hover:text-terracotta"
                data-testid="rebirthing-email"
              >
                newearth144@proton.me
              </a>
            </div>

            <div className="flex items-center gap-3">
              <Instagram
                className="h-4 w-4 text-terracotta"
                strokeWidth={1.5}
              />
              <a
                href="https://www.instagram.com/celestialseedcolombia2026?igsh=MzRlODBiNWFlZA=="
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-terracotta"
                data-testid="rebirthing-instagram"
              >
                @celestialseedcolombia2026
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-20">
        <div
          className="overline mb-3"
          data-testid="rebirthing-faq-overline"
        >
          {t("faq.overline", "FAQ")}
        </div>

        <h2
          className="font-serif text-4xl mb-8"
          data-testid="rebirthing-faq-headline"
        >
          {t("faq.headline", "Common questions")}
        </h2>

        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`item-${i}`}
              className="border-line"
            >
              <AccordionTrigger className="text-left font-serif text-xl">
                {f.q}
              </AccordionTrigger>

              <AccordionContent className="text-ink-soft leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}